import React, { useRef } from "react";
import { Button, LinearProgress, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import { useParams } from "react-router-dom";

import MetricCard from "../ui/MetricCard";
import EmptyState from "../ui/EmptyState";
import useSnack from "../ui/useSnack";
import Chart from "../charts/Chart";
import { saveChartImage } from "../../utils/saveChartImage";

/**
 * Shared shell for every live-capture panel.
 *
 * These panels open inside the "request data" dialog, so they use the same
 * MetricCard as the stored-record panels — a live reading and a saved one
 * should not look like two different products.
 */
const RealTimeCard = ({
  title,
  icon,
  accent,
  exportName,
  status,
  progress,
  stats,
  buildOption,
  deps = [],
  hasData,
  waitingCopy,
  height = 300,
}) => {
  const { watchId } = useParams();
  const chartRef = useRef(null);
  const { snack, showSnack } = useSnack();

  const capturing = progress > 0 && progress < 100;

  const handleExport = async () => {
    const result = await saveChartImage(chartRef, watchId, exportName);
    showSnack(
      result.ok
        ? "Chart saved — find it on the Files page."
        : "Could not export the chart image.",
      result.ok ? "success" : "error"
    );
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={icon}
        title={title}
        meta={
          <>
            <span className={`wg-chip ${capturing ? "wg-chip--accent" : ""}`}>
              <span
                className={`wg-chip__dot ${capturing ? "wg-chip__dot--pulse" : ""}`}
                style={capturing ? undefined : { background: "var(--wg-ink-muted)" }}
              />
              {capturing ? "Capturing" : progress === 100 ? "Complete" : "Standby"}
            </span>
            {progress > 0 && <span className="wg-chip">{progress}%</span>}
          </>
        }
        actions={
          <Tooltip title="Download a PNG and archive it under Files">
            <span>
              <Button
                size="small"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleExport}
                disabled={!hasData}
              >
                Export
              </Button>
            </span>
          </Tooltip>
        }
        note={status}
        stats={stats}
        empty={
          !hasData ? (
            <EmptyState
              icon={<SensorsOutlinedIcon />}
              title="Waiting for the watch"
              copy={waitingCopy}
            />
          ) : null
        }
        footer={
          capturing ? (
            <div style={{ width: "100%" }}>
              <LinearProgress variant="determinate" value={progress} />
              <p style={{ marginTop: 6 }}>Capture {progress}% complete</p>
            </div>
          ) : null
        }
      >
        <Chart
          buildOption={buildOption}
          chartRef={chartRef}
          height={height}
          deps={deps}
        />
      </MetricCard>

      {snack}
    </>
  );
};

export default RealTimeCard;
